import { MerlinCardAction, MerlinCardInfo } from 'types/edgeFunctions';

const CARD_COMPLETE_REGEX = /\[CARD_COMPLETE\]/;

/**
 * Check if text contains the [CARD_COMPLETE] marker.
 */
export const hasCardCompleteMarker = (text: string): boolean => CARD_COMPLETE_REGEX.test(text);

/**
 * Strip [CARD_COMPLETE] marker from text.
 */
export const stripCardCompleteMarker = (text: string): string =>
  text.replace(CARD_COMPLETE_REGEX, '').trim();

/**
 * Parse [CARD_COMPLETE] marker from Merlin message text.
 * Returns cleaned text (marker stripped) and cardAction if marker + currentCard present.
 */
export const parseCardAction = (
  text: string,
  currentCard?: MerlinCardInfo | null
): { cleanText: string; cardAction: MerlinCardAction | null } => {
  if (!currentCard || !CARD_COMPLETE_REGEX.test(text)) {
    return { cleanText: text, cardAction: null };
  }
  return {
    cleanText: text.replace(CARD_COMPLETE_REGEX, '').trim(),
    cardAction: { cardId: currentCard.id, cardTitle: currentCard.title },
  };
};

/**
 * Resolve card action with fallback heuristic.
 * Primary: [CARD_COMPLETE] marker in text.
 * Fallback: if current card exists AND code was generated, show button anyway.
 */
export const resolveCardAction = (
  text: string,
  currentCard: MerlinCardInfo | null | undefined,
  hasCodeChanges: boolean
): MerlinCardAction | null => {
  if (!currentCard) return null;

  // Primary: explicit marker
  if (hasCardCompleteMarker(text)) {
    return { cardId: currentCard.id, cardTitle: currentCard.title };
  }

  // Fallback: code was generated for a card context but marker missing
  if (hasCodeChanges) {
    return { cardId: currentCard.id, cardTitle: currentCard.title };
  }

  return null;
};

/**
 * Strip :::CODE_START...:::CODE_END blocks from older assistant messages
 * to reduce token usage in conversation history.
 * Keeps the most recent `keepRecent` messages intact so the model has recent code context.
 */
export const stripCodeFromOlderHistory = (
  history: { role: 'user' | 'assistant'; content: string }[],
  keepRecent = 2
): { role: 'user' | 'assistant'; content: string }[] => {
  if (history.length <= keepRecent) return history;

  const cutoff = history.length - keepRecent;
  return history.map((msg, i) => {
    if (msg.role === 'assistant' && i < cutoff) {
      return {
        ...msg,
        content: msg.content.replace(/:::CODE_START[\s\S]*?:::CODE_END/g, '[code applied]'),
      };
    }
    return msg;
  });
};
